import { useEffect, useState } from 'react';
import { LocalStorageService } from './services/local-storage.service';
import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom';
import { PROFILES, STORAGE } from './constants';
import { Toaster } from 'react-hot-toast';
import { HealthCheck } from './services/healt-check.service';
import './index.scss';
import './i18/i18';
import ReactDOM from 'react-dom/client';
import AuthPage from './pages/auth/login.page';
import DashboardPage from './pages/dashboard/dashboard.page';
import TemplatesRCSPage from './pages/templates/rcs/templates-rcs.page';
import BuilderBasic from './pages/templates/rcs/partial/builder-basic.partial';
import BuilderSimpleAndConversational from './pages/templates/rcs/partial/builder-single-conversational.partial';
import TemplatesWaPage from './pages/templates/wa/templates-wa.page';
import AccountsPage from './pages/accounts/accounts.page';
import ChannelsPanel from './pages/channels/principal-panel.page';
import NotFoundPage from './pages/errors/not-found.page';
import useBgPages from './hooks/use-bg-pages.hook';
import UsersPage from './pages/users/users.page';
import CampaignsPanel from './pages/campaings/sends/principal-panel.page';
import CampaignStandar from './pages/campaings/sends/standard/campaigns-page';
import CampaignFallback from './pages/campaings/sends/fallback/campaigns-page';
import CampaignFlows from './pages/campaings/sends/flow/campaigns-page';
import Resume from './pages/campaings/sends/resumen.page';
import RolesPage from './pages/roles/roles.page';
import BlackListPage from './pages/blacklist/blacklist.page';
import HasAccessRoute from './guard/has-access-route';
import CreateTemplatesWa from './pages/templates/wa/components/create-wa.component';
import VerifyPassword from './pages/auth/verify-password.page';
import ChannelsWA from './pages/channels/wa/channels-wa.page';
import ChannelsRCS from './pages/channels/rcs/channels-rcs-page';
import ChannelsSMS from './pages/channels/sms/channels-sms-page';
import ProfileSettings from './pages/profile/profile-settings.page';
import TemplatesPanel from './pages/templates/principal-panel.page';
import AddAccountViewWrapper from './pages/accounts/add-accountView-wrapper';
import ChannelsConfiguratorWrapper from './pages/accounts/channels-configurator-wrapper';
import FlowsPage from './pages/campaings/flows/flows.page';
import FlowsBuilderPage from './pages/campaings/flows/flows.builder';
import SelectAccount from './pages/auth/select-account.page';
import ChannelsMailingPage from './pages/channels/mailing/channels-mailing.page';
import TemplatesMailingPage from './pages/templates/mailing/templates-mailing';
import MailingBuilderPage from './pages/templates/mailing/mailing-builder.page';
import PNNPortability from './pages/pnn/pnn.page';

const PrivateRoute = () => {
  const [checking, setChecking] = useState(true);
  const [isValid, setIsValid] = useState(false);

  useEffect(() => {
    const validate = async () => {
      const session = LocalStorageService.get(STORAGE.SESSION);
      const token = LocalStorageService.get(STORAGE.TOKEN);

      if (window.location.pathname.startsWith('/recovery-password')) {
        setIsValid(true);
        setChecking(false);
        return;
      }

      if (!session || !token) {
        setIsValid(false);
        setChecking(false);
        return;
      }

      try {
        const response = await HealthCheck.validateToken();
        if (!response?.status) {
          setIsValid(false);
        } else {
          if (session?.profile === PROFILES.ADMIN) {
            const account = LocalStorageService.get(STORAGE.SELECTED_ACCOUNT);
            if (!account) {
              setIsValid(false);
              setChecking(false);
              return;
            }
          }
          setIsValid(true);
        }
      } catch (err) {
        setIsValid(false);
      } finally {
        setChecking(false);
      }
    };

    validate();
  }, []);

  if (checking) return null;

  if (!isValid) return <Navigate to="/auth/login" replace />;

  return <Outlet />;
};

const LayoutRoute = () => {
  useBgPages();

  return (
    <>
      <Outlet />
    </>
  );
};

const Layout = () => {
  return (
    <>
      <BrowserRouter>
        <Routes>
          <Route path="/auth">
            <Route path="login" element={<AuthPage />} />
          </Route>
          <Route path="/recovery-password/*" element={<VerifyPassword />} />
          <Route path="/select-account" element={<SelectAccount />} />
          <Route path="/" element={<PrivateRoute />}>
            <Route path="/" element={<LayoutRoute />}>
              <Route path="/" element={<DashboardPage />} />
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/profile-settings" element={<ProfileSettings />} />
              <Route
                path="/sendCampaigns"
                element={
                  <HasAccessRoute view={[PROFILES.ROOT, PROFILES.ADMIN, PROFILES.STANDARD_USER]} />
                }
              >
                <Route path="/sendCampaigns/standard-campaigns" element={<CampaignStandar />} />
                <Route path="/sendCampaigns/fallback-campaigns" element={<CampaignFallback />} />
                <Route path="/sendCampaigns/flows-campaigns" element={<CampaignFlows />} />
                <Route path="/sendCampaigns/flows-answers" element={<FlowsPage />} />
                <Route path="/sendCampaigns/flows-answers/builder" element={<FlowsBuilderPage />} />
                <Route path="/sendCampaigns/resume" element={<Resume />} />
                <Route path="" element={<CampaignsPanel />} />
              </Route>
              <Route
                path="/users"
                element={
                  <HasAccessRoute view={[PROFILES.ROOT, PROFILES.ADMIN, PROFILES.STANDARD_USER]} />
                }
              >
                <Route path="" element={<UsersPage />} />
              </Route>
              <Route
                path="/roles"
                element={<HasAccessRoute view={[PROFILES.ROOT, PROFILES.ADMIN]} />}
              >
                <Route index element={<RolesPage />} />
                <Route path="builder-role" element={<RolesPage />} />
              </Route>
              <Route path="/templates-panel/templates-rcs" element={<TemplatesRCSPage />} />
              <Route
                path="/templates-panel/templates-rcs-builder-basic"
                element={<BuilderBasic />}
              />
              <Route
                path="/templates-panel/templates-rcs-builder-single"
                element={<BuilderSimpleAndConversational />}
              />
              <Route
                path="/templates-panel/templates-rcs-builder-conversational"
                element={<BuilderSimpleAndConversational />}
              />

              <Route path="/templates-panel/templates-wa" element={<TemplatesWaPage />} />
              <Route path="/templates/create-template-WA" element={<CreateTemplatesWa />} />
              <Route path="/templates-panel/accounts-wa" element={<ChannelsWA />} />
              <Route
                path="/templates-panel"
                element={<HasAccessRoute view={[PROFILES.ROOT, PROFILES.ADMIN]} />}
              >
                <Route path="" element={<TemplatesPanel />} />
                <Route path="channels-wa" element={<ChannelsWA />} />
              </Route>
              <Route path="/templates-panel/templates-mailing" element={<TemplatesMailingPage />} />
              <Route
                path="/templates-panel/templates-mailing-builder"
                element={<MailingBuilderPage />}
              />
              <Route
                path="/blacklist"
                element={
                  <HasAccessRoute view={[PROFILES.ROOT, PROFILES.ADMIN, PROFILES.STANDARD_USER]} />
                }
              >
                <Route index element={<BlackListPage />} />
                <Route path="add-blacklist" element={<BlackListPage />} />
                <Route path="edit-blacklist" element={<BlackListPage />} />
              </Route>
              <Route path="/accounts" element={<HasAccessRoute view={[PROFILES.ROOT]} />}>
                <Route path="" element={<AccountsPage />} />
                <Route path="add" element={<AddAccountViewWrapper />} />
                <Route path="channels-configurator/:id" element={<ChannelsConfiguratorWrapper />} />
              </Route>
              <Route
                path="/channels-panel"
                element={<HasAccessRoute view={[PROFILES.ROOT, PROFILES.ADMIN]} />}
              >
                <Route path="" element={<ChannelsPanel />} />
                <Route path="channels-wa" element={<ChannelsWA />} />
                <Route path="channels-sms" element={<ChannelsSMS />} />
                <Route path="channels-rcs" element={<ChannelsRCS />} />
                <Route path="channels-mailing" element={<ChannelsMailingPage />} />
              </Route>
              <Route path="/pnn-portability" element={<HasAccessRoute view={[PROFILES.ROOT]} />}>
                <Route path="" element={<PNNPortability />} />
              </Route>
            </Route>
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster position="top-center" containerStyle={{ zIndex: 1 }} />
    </>
  );
};

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

root.render(<Layout />);
